import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { Outlet } from "react-router-dom";
export const ProductConfigurationContext = createContext();

// Utility: cartesian product
function cartesianProduct(arrays) {
  return arrays.reduce(
    (acc, curr) => acc.flatMap((a) => curr.map((b) => [...a, b])),
    [[]]
  );
}

export const ProductConfigurationProvider = () => {
  const [test, setTest] = useState("hello");

  // Selected Attributes State
  const [selectedAttributes, setSelectedAttributes] = useState([]);

  // Attribute Values State (for useAttributeValueLogic)
  const [attributeValues, setAttributeValues] = useState({});

  // Selected Attribute Values State (for checkbox selections)
  const [selectedAttributeValues, setSelectedAttributeValuesRaw] = useState({});
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Wrap the setter to force re-renders
  const setSelectedAttributeValues = (updater) => {
    if (typeof updater === "function") {
      setSelectedAttributeValuesRaw((prev) => {
        const result = updater(prev);
        // Force re-render
        setUpdateTrigger((t) => t + 1);
        return result;
      });
    } else {
      setSelectedAttributeValuesRaw(updater);
      setUpdateTrigger((t) => t + 1);
    }
  };

  // Updated Variant Configurations State (for storing form updates)
  const [updatedVariantConfigurations, setUpdatedVariantConfigurations] =
    useState([]);

  // Add debugging for navigation state changes
  useEffect(() => {
    console.log("📊 NAVIGATION DEBUG - selectedAttributeValues changed:");
    console.log("📊 Current state:", selectedAttributeValues);
    Object.keys(selectedAttributeValues).forEach((attrName) => {
      const values = selectedAttributeValues[attrName];
      console.log(
        `📊 ${attrName}: ${values.length} values`,
        values.map((v) => v.attribute_value_name)
      );
    });
  }, [selectedAttributeValues]);

  // Helper functions for managing selected attribute values
  const addSelectedAttributeValue = (attributeName, valueObject) => {
    setSelectedAttributeValues((prev) => ({
      ...prev,
      [attributeName]: [...(prev[attributeName] || []), valueObject],
    }));
  };

  const removeSelectedAttributeValue = (attributeName, valueId) => {
    setSelectedAttributeValues((prev) => ({
      ...prev,
      [attributeName]: (prev[attributeName] || []).filter(
        (val) => String(val.attribute_value_id) !== String(valueId)
      ),
    }));
  };

  const toggleSelectedAttributeValue = (attributeName, valueObject) => {
    console.log("🚀 SIMPLE TOGGLE - Start", { attributeName, valueObject });

    setSelectedAttributeValuesRaw((currentState) => {
      console.log("🔍 Current state before toggle:", currentState);

      const currentValues = currentState[attributeName] || [];
      console.log("🔍 Current values for attribute:", currentValues);

      const valueId = String(valueObject.attribute_value_id);
      const existingIndex = currentValues.findIndex(
        (val) => String(val.attribute_value_id) === valueId
      );

      console.log("🔍 Existing index:", existingIndex);

      let newValues;
      if (existingIndex >= 0) {
        // Remove existing value
        newValues = currentValues.filter((_, index) => index !== existingIndex);
        console.log("➖ Removing value, new array:", newValues);
      } else {
        // Add new value
        newValues = [...currentValues, valueObject];
        console.log("➕ Adding value, new array:", newValues);
      }

      const newState = {
        ...currentState,
        [attributeName]: newValues,
      };

      console.log("📦 Final new state:", newState);
      return newState;
    });

    // Force update trigger
    setUpdateTrigger((prev) => prev + 1);

    console.log("🚀 SIMPLE TOGGLE - End");
  };

  const selectAllAttributeValues = (attributeName, valueObjects) => {
    setSelectedAttributeValues((prev) => ({
      ...prev,
      [attributeName]: valueObjects,
    }));
  };

  const clearSelectedAttributeValues = (attributeName) => {
    setSelectedAttributeValues((prev) => ({
      ...prev,
      [attributeName]: [],
    }));
  };

  const isAttributeValueSelected = (attributeName, valueId) => {
    const current = selectedAttributeValues[attributeName] || [];
    return current.some(
      (val) => String(val.attribute_value_id) === String(valueId)
    );
  };

  const getSelectedAttributeValues = (attributeName) => {
    // Force re-read by including updateTrigger (not used but ensures freshness)
    return selectedAttributeValues[attributeName] || [];
  };

  // Helper functions for managing selected attributes
  const addSelectedAttribute = (attributeObject) => {
    setSelectedAttributes((prev) => {
      const isAlreadySelected = prev.some(
        (attr) => attr.attribute_id === attributeObject.attribute_id
      );
      if (!isAlreadySelected) {
        return [...prev, attributeObject];
      }
      return prev;
    });
  };

  const removeSelectedAttribute = (attributeId) => {
    setSelectedAttributes((prev) =>
      prev.filter((attr) => attr.attribute_id !== attributeId)
    );
  };

  const toggleSelectedAttribute = (attributeObject) => {
    setSelectedAttributes((prev) => {
      const isSelected = prev.some(
        (attr) => attr.attribute_id === attributeObject.attribute_id
      );
      if (isSelected) {
        return prev.filter(
          (attr) => attr.attribute_id !== attributeObject.attribute_id
        );
      } else {
        return [...prev, attributeObject];
      }
    });
  };

  const setAllSelectedAttributes = (attributesArray) => {
    setSelectedAttributes(attributesArray);
  };

  const clearSelectedAttributes = () => {
    setSelectedAttributes([]);
  };

  const isAttributeSelected = (attributeId) => {
    return selectedAttributes.some((attr) => attr.attribute_id === attributeId);
  };

  const getSelectedAttributeIds = () => {
    return selectedAttributes.map((attr) => attr.attribute_id);
  };

  // Generate variant configurations from selected attributes and values
  const generateVariantConfigurations = useCallback(
    (productName = "") => {
      console.log("🏭 generateVariantConfigurations called");
      console.log("🏭 selectedAttributes:", selectedAttributes);
      console.log("🏭 selectedAttributeValues:", selectedAttributeValues);

      // Get arrays of selected values for each attribute from context
      const selectedValuesArrays = selectedAttributes
        .map((attr) => {
          const values = selectedAttributeValues[attr.attribute_name] || [];
          console.log(`🏭 ${attr.attribute_name} selected values:`, values);
          return values.map((val) => ({
            ...val,
            // Ensure attribute_id is set from the parent attribute
            attribute_id: val.attribute_id || attr.attribute_id,
          }));
        })
        .filter((arr) => arr.length > 0);

      console.log("🏭 selectedValuesArrays:", selectedValuesArrays);

      // Generate all combinations
      const combinations = cartesianProduct(selectedValuesArrays);
      console.log("🏭 combinations:", combinations);

      // Build config array
      const config = combinations.map((combo) => ({
        attributes: combo.map((v) => ({
          attribute_value_name: v.attribute_value_name || v.name,
          attribute_value_id: v.attribute_value_id,
          attribute_name: v.attribute_value_name,
          attribute_id: v.attribute_id,
          in_stock: true,
        })),
        images_of_product: [],
        images_of_product_preview: [],
        selling_price: "",
        business_selling_price: "",
        mrp_price: "",
        quantity: "",
        status: "1",
        product_map_id: "",
        product_name: productName,
      }));

      console.log("🏭 final config:", config);
      return config;
    },
    [selectedAttributes, selectedAttributeValues]
  );

  // Get the final variant configurations (updated or generated)
  const getFinalVariantConfigurations = useCallback(
    (productName = "") => {
      console.log("🎯 getFinalVariantConfigurations called");
      console.log(
        "🎯 updatedVariantConfigurations.length:",
        updatedVariantConfigurations.length
      );

      // If we have updated configurations, return those
      if (updatedVariantConfigurations.length > 0) {
        console.log("🎯 Returning updated configurations");
        return updatedVariantConfigurations;
      }
      // Otherwise generate fresh ones
      console.log("🎯 Generating fresh configurations");
      return generateVariantConfigurations(productName);
    },
    [updatedVariantConfigurations, generateVariantConfigurations]
  );

  const contextValue = {
    test,
    setTest,

    // Selected Attributes
    selectedAttributes,
    setSelectedAttributes,

    // Attribute Values
    attributeValues,
    setAttributeValues,

    // Selected Attribute Values
    selectedAttributeValues,
    setSelectedAttributeValues,

    // Helper Methods for Attributes
    addSelectedAttribute,
    removeSelectedAttribute,
    toggleSelectedAttribute,
    setAllSelectedAttributes,
    clearSelectedAttributes,
    isAttributeSelected,
    getSelectedAttributeIds,

    // Helper Methods for Attribute Values
    addSelectedAttributeValue,
    removeSelectedAttributeValue,
    toggleSelectedAttributeValue,
    selectAllAttributeValues,
    clearSelectedAttributeValues,
    isAttributeValueSelected,
    getSelectedAttributeValues,

    // Variant Configuration Generation
    generateVariantConfigurations,
    getFinalVariantConfigurations,
    updatedVariantConfigurations,
    setUpdatedVariantConfigurations,
  };

  // Reset previously updated configurations whenever the attribute selection changes
  // This ensures Summary regenerates combinations to reflect the latest selection
  useEffect(() => {
    setUpdatedVariantConfigurations([]);
  }, [selectedAttributes, selectedAttributeValues]);

  return (
    <ProductConfigurationContext.Provider value={contextValue}>
      <Outlet />
    </ProductConfigurationContext.Provider>
  );
};
